/**
 * Utility functions for handling field values in the application
 */

/**
 * Gets a formatted value from a row object based on field configuration
 * @param {Object} row - The row object containing the data
 * @param {string} fieldPath - The field path (e.g., "Related_Account__r.Name")
 * @param {Object} field - The field configuration object
 * @returns {string|number|Date} The formatted value
 */
export const getFieldValue = (row, fieldPath, field) => {
    // Handle nested fields
    const getNestedValue = (obj, path) => {
        const parts = path?.split('.')||[];
        let value = obj;
        for (const part of parts) {
            value = value?.[part];
            if (value === undefined || value === null) return '';
        }
        return value;
    };

    const value = getNestedValue(row, fieldPath);

    // Handle different field types
    if (field.type === 'date' || fieldPath?.toLowerCase().includes('date')) {
        return value ? new Date(value).toLocaleDateString() : '';
    }

    if (field.type === 'datetime' || fieldPath?.toLowerCase().includes('datetime')) {
        return value ? new Date(value).toLocaleString() : '';
    }

    if (field.type === 'currency' || fieldPath?.toLowerCase().includes('amount')) {
        return value ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value) : '';
    }

    if (field.type === 'number') {
        return value ? Number(value).toLocaleString() : '';
    }

    if (field.type === 'boolean') {
        return value ? 'Yes' : 'No';
    }

    return value;
};

/**
 * Creates a column configuration for a field
 * @param {Object} field - The field configuration object
 * @returns {Object} The column configuration
 */
export const createColumnConfig = (field) => ({
    id: field.field,
    name: field.label,
    selector: field.selector || (row => getFieldValue(row, field.field, field)),
    cell: field.cell || (row => getFieldValue(row, field.field, field)),
    sortable: true,
    width: '300px'
});

/**
 * Creates column configurations for a list of fields
 * @param {Array} fields - Array of field configuration objects
 * @returns {Array} Array of column configurations
 */
export const createColumnConfigs = (fields) => 
    fields.map(field => createColumnConfig(field)).filter(Boolean);  

/**
 * Formats a date value
 * @param {string|Date} value - The date value to format
 * @param {string} [format='date'] - The format type ('date' or 'datetime')
 * @returns {string} The formatted date string
 */
export const formatDate = (value, format = 'date') => {
    if (!value) return '';
    const date = new Date(value);
    return format === 'datetime' ? date.toLocaleString() : date.toLocaleDateString();
};

/**
 * Formats a currency value
 * @param {number} value - The number to format
 * @param {string} [currency='USD'] - The currency code
 * @returns {string} The formatted currency string
 */
export const formatCurrency = (value, currency = 'USD') => {
    if (!value) return '';
    return new Intl.NumberFormat('en-US', { 
        style: 'currency', 
        currency 
    }).format(value);
};

/**
 * Formats a number value
 * @param {number} value - The number to format
 * @returns {string} The formatted number string
 */
export const formatNumber = (value) => {
    if (!value) return '';
    return Number(value).toLocaleString();
};

/**
 * Formats a boolean value
 * @param {boolean} value - The boolean to format
 * @returns {string} The formatted boolean string ('Yes' or 'No')
 */
export const formatBoolean = (value) => {
    return value ? 'Yes' : 'No';
}; 